import React, { useCallback, useState, useRef } from 'react';
import { Upload, AlertTriangle, ShieldAlert, Lock, ArrowRight } from 'lucide-react';
import { FloatingElements } from './FloatingElements';
import { downloadSampleReport } from '../../utils/sampleReportGenerator';
import { useSmartFeedback } from '../../contexts/SmartFeedbackContext';
interface HeroSectionProps {
  h1Override?: React.ReactNode;
}
/**
 * PERF NOTES — mobile 3G/4G optimization:
 *  - Removed framer-motion entirely. Entrance animations are now CSS @keyframes
 *    with staggered animation-delay. Saves ~50KB JS init + main-thread work
 *    before LCP.
 *  - DOM hierarchy flattened (removed redundant wrappers).
 *  - File input uses ref + useCallback so the handler isn't recreated each render.
 *  - All decorative effects are pure CSS.
 *  - Hero "scan result" image kept (it's the strongest social-proof visual),
 *    but with proper width/height to avoid CLS and `loading="eager"
 *    fetchpriority=high` so it's prioritized.
 */
export const HeroSection = ({
  h1Override
}: HeroSectionProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pastedText, setPastedText] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const {
    openLandingFeedback
  } = useSmartFeedback();
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setSelectedFile(f);
  }, []);
  const handleDownloadSampleReport = useCallback(async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadSampleReport();
    } catch (err) {
      console.error('Failed to download sample report:', err);
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading]);
  return <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 bg-[#050505] overflow-hidden min-h-[90vh] flex flex-col justify-center" data-id="element-2558">
      <FloatingElements data-id="element-2559" />

      {/* Grid background — pure CSS, GPU-friendly, no extra DOM nodes */}
      <div className="absolute inset-0 pointer-events-none z-0" aria-hidden="true" style={{
      backgroundImage: 'linear-gradient(rgba(20,20,20,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(20,20,20,0.5) 1px,transparent 1px)',
      backgroundSize: '40px 40px',
      maskImage: 'radial-gradient(ellipse 80% 50% at 50% 50%,#000 70%,transparent 100%)',
      WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 50%,#000 70%,transparent 100%)'
    }} data-id="element-2560" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center w-full" data-id="element-2561">
        {/* Status badge */}
        <span className="hero-fade-up hero-d1 inline-flex items-center gap-2 bg-[#FF0000]/10 text-[#FF0000] px-4 py-1.5 rounded-full border border-[#FF0000]/30 mb-8 shadow-[0_0_15px_rgba(255,0,0,0.15)]" data-id="element-2562">
          <AlertTriangle size={14} strokeWidth={2.5} className="hero-pulse" data-id="element-2563" />
          <span className="font-mono text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase" data-id="element-2564">
            Forensic AI Analysis Active
          </span>
        </span>

        {/* Headline */}
        <h1 className="hero-fade-up hero-d2 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-[1.05] tracking-tight max-w-5xl mx-auto" data-id="element-2565">
          {h1Override || <>
              Detect Manipulation & Gaslighting in{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF0000] via-[#FF4444] to-[#FFD700]" data-id="element-2566">
                Conversations
              </span>
            </>}
        </h1>

        {/* Subheadline */}
        <p className="hero-fade-up hero-d3 text-base md:text-lg text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed font-mono" data-id="element-2567">
          Upload your chats and let{' '}
          <span className="text-[#FFD700] font-bold" data-id="element-2568">forensic AI</span> surface
          manipulation, gaslighting, and emotional abuse —{' '}
          <span className="text-white" data-id="element-2569">
            with the clarity of a clinical analyst
          </span>
          .
        </p>

        {/* Scan result image — kept because it's the strongest social proof on the page */}
        <figure className="hero-fade-up hero-d4 relative max-w-[480px] mx-auto mb-10 group" data-id="element-2570">
          <div className="absolute -inset-1 bg-[#FF0000]/20 rounded-xl blur-xl opacity-50 group-hover:opacity-100 transition-opacity duration-500" aria-hidden="true" data-id="element-2571" />
          <div className="relative rounded-xl overflow-hidden border border-[#FF0000]/30 shadow-[0_0_30px_rgba(255,0,0,0.18)] bg-black/50" data-id="element-2572">
            <img src="https://cdn.magicpatterns.com/uploads/6fWHFbe1LodeRZcxwg7PYR/file_00000000970c722f86dc01c42d5e2c9f_(1).png" alt="Toxicity Detected 92% — Forensic Scan Result" className="w-full h-auto object-cover opacity-90" width={480} height={380} loading="eager" fetchPriority="high" decoding="async" data-id="element-2573" />
            <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent p-4 flex justify-between items-end" data-id="element-2574">
              <span className="text-[#FF0000] font-mono text-[10px] tracking-[0.2em] font-bold uppercase flex items-center gap-2" data-id="element-2575">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF0000] hero-pulse-dot" aria-hidden="true" data-id="element-2576" />
                Real Scan Result
              </span>
              <span className="text-white/50 font-mono text-[10px] tracking-[0.2em]" data-id="element-2577">
                ID: #8X92-A
              </span>
            </figcaption>
          </div>
        </figure>

        {/* Forensic Scanner Widget — flattened, no framer-motion */}
        <div className="hero-fade-up hero-d5 relative max-w-2xl mx-auto mb-10" data-id="element-2578">
          <div className="absolute -inset-1 bg-gradient-to-r from-[#FF0000] via-[#FFD700] to-[#FF0000] rounded-2xl opacity-20 blur-xl hero-pulse-bg" aria-hidden="true" data-id="element-2579" />

          <div className="relative bg-[#0a0a0a] rounded-xl border border-[#FF0000]/30 p-1 overflow-hidden shadow-2xl" data-id="element-2580">
            {/* Corner Markers */}
            <span className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#FF0000] z-20 rounded-tl-lg" aria-hidden="true" data-id="element-2581" />
            <span className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#FF0000] z-20 rounded-tr-lg" aria-hidden="true" data-id="element-2582" />
            <span className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#FF0000] z-20 rounded-bl-lg" aria-hidden="true" data-id="element-2583" />
            <span className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#FF0000] z-20 rounded-br-lg" aria-hidden="true" data-id="element-2584" />

            <div className="bg-[#050505] rounded-lg p-6 md:p-8 text-center relative overflow-hidden" data-id="element-2585">
              {/* Scanline */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-[#FF0000] shadow-[0_0_20px_#FF0000] z-10 hero-scanline" aria-hidden="true" data-id="element-2586" />

              {/* Inner grid */}
              <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
              backgroundImage: 'linear-gradient(rgba(255,0,0,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,0,0,0.05) 1px,transparent 1px)',
              backgroundSize: '20px 20px'
            }} data-id="element-2587" />

              <div className="relative z-20 mb-6 text-left" data-id="element-2588">
                <textarea value={pastedText} onChange={e => setPastedText(e.target.value)} className="w-full h-32 bg-[#0a0a0a] border border-gray-800 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white p-4 font-mono text-sm resize-none rounded-lg transition-colors" placeholder="Paste your chat conversation here (e.g., texts, emails, or messages) to begin your forensic analysis..." data-id="element-2589" />
                <p className="text-[11px] text-gray-500 font-mono mt-2" data-id="element-2590">
                  Fast & Simple: Just copy the messages from your chat app and
                  paste them right here.
                </p>
                {pastedText.trim().length > 0 && <a href="/signup" className="mt-4 block w-full py-3 bg-[#FF0000] text-white font-bold uppercase tracking-wider text-center rounded-lg hover:bg-red-600 transition-colors shadow-[0_0_15px_rgba(255,0,0,0.3)] animate-[heroFadeUp_0.3s_ease-out_forwards]" data-id="element-2591">
                    Analyze Text Now
                  </a>}
              </div>

              <div className="relative z-20 flex items-center gap-4 mb-6" data-id="element-2592">
                <div className="h-px bg-gray-800 flex-1" data-id="element-2593"></div>
                <span className="text-xs text-gray-600 font-mono uppercase tracking-widest" data-id="element-2594">
                  OR
                </span>
                <div className="h-px bg-gray-800 flex-1" data-id="element-2595"></div>
              </div>

              <input ref={fileInputRef} type="file" id="hero-file-upload" className="hidden" onChange={handleFileChange} accept="image/*,.pdf" data-id="element-2596" />
              <label htmlFor="hero-file-upload" className="cursor-pointer block relative z-20 group/label" data-id="element-2597">
                <span className="inline-flex items-center justify-center w-16 h-16 bg-[#FF0000]/10 rounded-full mb-4 border border-[#FF0000]/50 group-hover/label:bg-[#FF0000]/20 transition-colors hero-dropglow" data-id="element-2598">
                  <Upload className="w-6 h-6 text-[#FF0000]" strokeWidth={2} data-id="element-2599" />
                </span>

                <h3 className="text-xl font-black text-white mb-2 uppercase tracking-wide group-hover/label:text-[#FF0000] transition-colors" data-id="element-2600">
                  Upload Screenshots
                </h3>

                {selectedFile ? <span className="inline-flex items-center gap-2 bg-[#FF0000]/20 border border-[#FF0000] px-4 py-2 rounded-lg text-[#FF0000] font-mono text-sm font-bold mt-2" data-id="element-2601">
                    <ShieldAlert size={16} data-id="element-2602" />
                    <span className="truncate max-w-[280px]" data-id="element-2603">
                      EVIDENCE LOADED: {selectedFile.name}
                    </span>
                  </span> : <span className="inline-flex items-center gap-2 text-[10px] text-gray-500 font-mono border border-gray-800 px-3 py-1.5 rounded-lg bg-black/50 mt-2" data-id="element-2604">
                    <Lock size={10} data-id="element-2605" />
                    <span className="tracking-[0.15em]" data-id="element-2606">
                      100% ANONYMOUS &amp; ENCRYPTED
                    </span>
                  </span>}
              </label>
            </div>
          </div>

          <div className="flex justify-between items-center mt-4 px-2" data-id="element-2607">
            <span className="text-[10px] text-[#FF0000] font-mono flex items-center gap-2 tracking-[0.2em] uppercase" data-id="element-2608">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF0000] hero-pulse-dot" aria-hidden="true" data-id="element-2609" />
              System Active
            </span>
            <span className="text-[10px] text-gray-600 font-mono tracking-[0.2em] uppercase" data-id="element-2610">
              V.2.4.0 Forensic Engine
            </span>
          </div>
        </div>

        {/* CTA Row */}
        <div className="hero-fade-up hero-d6 flex flex-col sm:flex-row flex-wrap gap-4 justify-center items-center mb-12" data-id="element-2611">
          <a href="/signup" className="group relative px-8 py-4 bg-[#FF0000] text-white font-bold text-base uppercase tracking-[0.15em] overflow-hidden rounded-xl hover:bg-red-600 transition-all shadow-[0_0_20px_rgba(255,0,0,0.3)] hover:shadow-[0_0_40px_rgba(255,0,0,0.5)] min-w-[240px] hover:-translate-y-0.5 inline-flex items-center justify-center gap-2" data-id="element-2612">
            <span data-id="element-2613">Start Forensic Scan</span>
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-2614" />
          </a>

          <button onClick={handleDownloadSampleReport} disabled={isDownloading} className="px-8 py-4 bg-transparent border border-gray-800 text-gray-300 font-bold text-base uppercase tracking-[0.15em] rounded-xl hover:border-[#FFD700] hover:text-[#FFD700] transition-all min-w-[240px] hover:-translate-y-0.5 hover:bg-[#FFD700]/5 disabled:opacity-50 disabled:cursor-wait" data-id="element-2615">
            {isDownloading ? 'Generating PDF…' : 'View Sample Report'}
          </button>

          <button type="button" onClick={openLandingFeedback} className="px-8 py-4 bg-transparent border border-gray-800 text-gray-300 font-bold text-base uppercase tracking-[0.15em] rounded-xl hover:border-white hover:text-white transition-all min-w-[240px] hover:-translate-y-0.5 hover:bg-white/5 inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-2616">
            Share Your Story
          </button>
        </div>

        {/* Live Stats Row */}
        <div className="hero-fade-up hero-d7 flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-8 text-xs sm:text-sm text-gray-500 font-mono" data-id="element-2617">
          <div className="flex items-center gap-3" data-id="element-2618">
            <span className="relative flex h-2 w-2" aria-hidden="true" data-id="element-2619">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#FFD700] opacity-75 hero-ping" data-id="element-2620" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FFD700]" data-id="element-2621" />
            </span>
            <span className="tracking-[0.15em] uppercase" data-id="element-2622">
              <span className="text-[#FFD700] font-bold" data-id="element-2623">10,000+</span>{' '}
              <span className="text-gray-400" data-id="element-2624">Conversations Analyzed</span>
            </span>
          </div>

          <div className="hidden sm:block w-px h-4 bg-gray-800" aria-hidden="true" data-id="element-2625" />

          <div className="tracking-[0.15em] uppercase" data-id="element-2626">
            <span className="text-white font-bold" data-id="element-2627">4.9/5</span>{' '}
            <span className="text-gray-500" data-id="element-2628">Accuracy Rating</span>
          </div>

          <div className="hidden sm:block w-px h-4 bg-gray-800" aria-hidden="true" data-id="element-2629" />

          <div className="flex items-center gap-2 tracking-[0.15em] uppercase" data-id="element-2630">
            <Lock size={12} className="text-gray-600" data-id="element-2631" />
            <span data-id="element-2632">AES-256 Encryption</span>
          </div>
        </div>
      </div>

      {/* All hero animations inlined as CSS — no JS animation runtime needed */}
      <style data-id="element-2633">{`
        .hero-fade-up { opacity: 0; transform: translateY(20px); animation: heroFadeUp .6s cubic-bezier(.22,1,.36,1) forwards; }
        .hero-d1 { animation-delay: 0s; }
        .hero-d2 { animation-delay: .1s; }
        .hero-d3 { animation-delay: .2s; }
        .hero-d4 { animation-delay: .3s; }
        .hero-d5 { animation-delay: .45s; }
        .hero-d6 { animation-delay: .6s; }
        .hero-d7 { animation-delay: .85s; }
        @keyframes heroFadeUp { to { opacity: 1; transform: translateY(0); } }

        .hero-pulse { animation: heroPulse 1.5s ease-in-out infinite; }
        @keyframes heroPulse { 0%, 100% { opacity: 1 } 50% { opacity: .4 } }

        .hero-pulse-dot { animation: heroPulseDot 1.4s ease-in-out infinite; box-shadow: 0 0 6px currentColor; }
        @keyframes heroPulseDot { 0%, 100% { opacity: 1 } 50% { opacity: .35 } }

        .hero-pulse-bg { animation: heroPulseBg 3s ease-in-out infinite; }
        @keyframes heroPulseBg { 0%, 100% { opacity: .2 } 50% { opacity: .35 } }

        .hero-scanline { animation: heroScanline 3s ease-in-out infinite; }
        @keyframes heroScanline {
          0% { top: 0; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }

        .hero-dropglow {
          animation: heroDropglow 2.5s ease-in-out infinite;
        }
        @keyframes heroDropglow {
          0%, 100% { box-shadow: 0 0 20px rgba(255,0,0,.25), inset 0 0 20px rgba(255,0,0,.1); transform: scale(1); }
          50% { box-shadow: 0 0 35px rgba(255,0,0,.5), inset 0 0 25px rgba(255,0,0,.2); transform: scale(1.06); }
        }

        .hero-ping { animation: heroPing 1.6s cubic-bezier(0,0,.2,1) infinite; }
        @keyframes heroPing {
          75%, 100% { transform: scale(2); opacity: 0; }
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-fade-up, .hero-pulse, .hero-pulse-dot, .hero-pulse-bg,
          .hero-scanline, .hero-dropglow, .hero-ping {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }
        }
      `}</style>
    </section>;
};